import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay
} from "@chakra-ui/react";
import { colours, fonts } from "@shared/ui.design-system";

/**
 * Props for the OperationSuccessModal component
 */
type Props = {
  /** Callback function called when the modal is closed  */
  onClose: () => void;
  /** The success message to display in the modal body */
  messageToShow: string;
};

/**
 * OperationSuccessModal - A modal component for displaying operation success messages
 *
 * This component renders a Chakra UI modal that displays a success message to the user.
 * The modal automatically shows when a message is provided and can be closed by clicking
 * the close button or clicking outside the modal content.
 *
 * @example
 * ```tsx
 * <OperationSuccessModal
 *   messageToShow="Your address has been updated successfully!"
 *   onClose={() => setIsModalOpen(false)}
 * />
 * ```
 *
 * @param props - The component props
 * @param props.onClose - Function called when modal is closed
 * @param props.messageToShow - Success message to display
 * @returns A React component that renders a success modal
 */
export const OperationSuccessModal = ({ onClose, messageToShow }: Props) => {
  return (
    <Modal isOpen={!!messageToShow} onClose={onClose}>
      <ModalOverlay bgColor={colours.transparency.dark} />
      <ModalContent mt={0}>
        <ModalCloseButton
          border="1px solid"
          borderRadius="50%"
          size="sm"
          top={-2}
          right={-2}
        />
        <ModalBody fontFamily={fonts.medium}>{messageToShow}</ModalBody>
      </ModalContent>
    </Modal>
  );
};
